import * as React from 'react';
import { Grid } from '@mui/material';
import { FormInput } from '~/components/FormComponents/FormInput';
import { FormYesNoQuestion } from '~/components/FormComponents/FormYesNoQuestion';
import { SharedRangeGroup } from './SharedGroupRange';
import { FieldErrors, useFormContext, useWatch } from 'react-hook-form';
import { TOwner, TOwnershipStructure } from '../../../types';
import { ErrorInfoMessage } from '../../OwnerCard/ErrorInfoMessage';
import { useGetOwnerIndex } from '../../../hooks/useGetOwnerIndex';
import { ForbidenCountriesOwners } from './ForbidenCountriesOwners';
import { useState } from 'react';
import { DeleteModal } from '~/components/modals/DeleteModal/DeleteModal';
import FormGridItem from '~/components/FormComponents/FormGridItem';

interface ISharedGroupOwnerFormProps {
  index: number;
  handlePercentageChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

export const SharedGroupOwnerForm = ({
  index,
  handlePercentageChange,
}: ISharedGroupOwnerFormProps) => {
  const {
    formState: { errors },
    trigger,
    setValue,
    control,
  } = useFormContext<TOwnershipStructure>();
  const { ownerIndex } = useGetOwnerIndex(index);
  const [showModal, setShowModal] = useState(false);

  const [
    hasRussianOrBelarusianCitizenShareholders,
    russianBelarusianLegalEntities,
    russianBelarusianIndividuals,
  ] = useWatch({
    control,
    name: [
      `owners.${ownerIndex}.hasRussianOrBelarusianCitizenShareholders`,
      `owners.${ownerIndex}.russianBelarusianLegalEntities`,
      `owners.${ownerIndex}.russianBelarusianIndividuals`,
    ],
  });

  const error = (errors.owners?.[ownerIndex] as FieldErrors<TOwner & { shares: string }>)?.shares
    ?.message;

  const handleDeleteItems = () => {
    setValue(`owners.${ownerIndex}.russianBelarusianIndividuals`, null);
    setValue(`owners.${ownerIndex}.russianBelarusianLegalEntities`, null);
    setValue(`owners.${ownerIndex}.hasRussianOrBelarusianCitizenShareholders`, false);
    setShowModal(false);
  };

  const handleIsForbidenOwnersChange = (value: boolean) => {
    if (
      !value &&
      (russianBelarusianLegalEntities?.length || russianBelarusianIndividuals?.length)
    ) {
      setShowModal(true);
      return;
    }
    setValue(`owners.${ownerIndex}.hasRussianOrBelarusianCitizenShareholders`, value);
  };

  return (
    <Grid container spacing={3} sx={{ width: '100%' }}>
      <Grid container sx={{ width: '100%', position: 'relative' }}>
        <SharedRangeGroup
          rangePath={`owners.${ownerIndex}.rangeLegal`}
          onBlur={() => trigger(`owners.${ownerIndex}.shares` as keyof typeof error)}
          ownerLabel="ЮО"
        />
        <SharedRangeGroup
          rangePath={`owners.${ownerIndex}.rangeIndividual`}
          onBlur={() => trigger(`owners.${ownerIndex}.shares` as keyof typeof error)}
          ownerLabel="ФО"
        />
        {error && <ErrorInfoMessage error={error} />}
      </Grid>
      <FormGridItem>
        <FormInput
          name={`link.${index}.ownershipPercentage`}
          placeholder="Введіть частку в статутному капіталі"
          label="Частка в статутному капіталі (%)"
          fullWidth
          disableFormOnChange
          onChange={handlePercentageChange}
        />
      </FormGridItem>
      <Grid size={{ xs: 0, sm: 0, md: 6 }} />
      <FormGridItem>
        <FormYesNoQuestion
          name={`owners.${ownerIndex}.hasRussianOrBelarusianCitizenShareholders`}
          question="Чи є серед акціонерів громадяни та/або резиденти РФ чи РБ?"
          disableFieldOnChange
          onChange={handleIsForbidenOwnersChange}
        />
      </FormGridItem>
      {hasRussianOrBelarusianCitizenShareholders && (
        <Grid size={12}>
          <ForbidenCountriesOwners ownerIndex={ownerIndex} />
        </Grid>
      )}
      <DeleteModal
        closeDeleteModal={() => setShowModal(false)}
        confirmDelete={handleDeleteItems}
        showDeleteModal={showModal}
        title='Якщо ви змінюєте відповідь на "Ні", всі зазначені особи будуть видалені. Продовжити?'
        confirmBtnText="Продовжити"
        cancelBtnText="Скасувати"
        hideIcon
      />
    </Grid>
  );
};
